import { cn } from "@design-system/utils";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

import { Badge } from "../badge";
import { TabsScrollButton } from "../tabs/tabs-scroll-button";
import {
  tabsItemVariants,
  tabsScrollerVariants,
  tabsWrapperVariants,
} from "../tabs/tabs-variants";
import {
  getScrollBehavior,
  scrollActiveTabIntoView,
  SCROLL_STEP_RATIO,
  useTabsOverflow,
} from "../tabs/use-tabs-overflow";

function TabNav({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root>) {
  return (
    <NavigationMenuPrimitive.Root
      data-slot="tab-nav"
      className={cn("flex w-full min-w-0 flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabNavList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const scrollerRef = React.useRef<HTMLUListElement | null>(null);
  const { canScrollLeft, canScrollRight, hasOverflow } = useTabsOverflow(
    wrapperRef,
    scrollerRef
  );

  // Keyboard arrow navigation only moves focus; the active-prop effect on
  // each item doesn't fire for focus changes, so scroll the focused item
  // into view here.
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onFocusIn = (e: FocusEvent) => {
      const item = (e.target as Element | null)?.closest<HTMLElement>(
        '[data-slot="tab-nav-item"]'
      );
      if (item) scrollActiveTabIntoView(item, '[data-slot="tab-nav-list"]');
    };
    scroller.addEventListener("focusin", onFocusIn);
    return () => scroller.removeEventListener("focusin", onFocusIn);
  }, []);

  const handleScrollBy = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: el.clientWidth * SCROLL_STEP_RATIO * direction,
      behavior: getScrollBehavior(),
    });
  };

  return (
    <div
      ref={wrapperRef}
      data-slot="tab-nav-list-wrapper"
      // Radix's NavigationMenu.List renders as `<div style="position:relative">
      // <ul>…</ul></div>` (the div is the indicator-track wrapper). Without a
      // width constraint on that div, the inner <ul>'s `w-full` resolves to
      // the <ul>'s content width and `overflow-x-auto` never engages. Force
      // the inserted div to fill the wrapper so the <ul>'s scroller works.
      className={cn(
        tabsWrapperVariants(),
        "[&>div]:flex [&>div]:w-full [&>div]:min-w-0",
        className
      )}
    >
      {/*
        Always render the scroll buttons so the wrapper's children structure
        stays stable when `hasOverflow` flips. Otherwise React reconciles by
        index and remounts `NavigationMenuPrimitive.List`, which would orphan
        the `useTabsOverflow` scroll / ResizeObserver listeners and the
        focus-driven scroll handler on the old DOM node.
      */}
      <TabsScrollButton
        direction="left"
        disabled={!hasOverflow || !canScrollLeft}
        onClick={() => handleScrollBy(-1)}
        dataSlot="tab-nav-scroll-button-left"
      />
      {/*
        `{...props}` stays on the Radix List so Radix-specific props and
        accessibility attributes reach the actual nav-list element. Only
        `className` is hoisted to the outer wrapper so consumer layout
        classes (spacing, width) override the outermost rendered element.
      */}
      <NavigationMenuPrimitive.List
        ref={scrollerRef}
        data-slot="tab-nav-list"
        className={tabsScrollerVariants()}
        {...props}
      >
        {children}
      </NavigationMenuPrimitive.List>
      <TabsScrollButton
        direction="right"
        disabled={!hasOverflow || !canScrollRight}
        onClick={() => handleScrollBy(1)}
        dataSlot="tab-nav-scroll-button-right"
      />
    </div>
  );
}

interface TabNavItemProps extends Omit<
  React.ComponentProps<typeof NavigationMenuPrimitive.Link>,
  "asChild" | "children"
> {
  children: React.ReactNode;
  icon?: LucideIcon;
  badge?: string | number;
  active?: boolean;
  disabled?: boolean;
}

function TabNavItem({
  className,
  children,
  icon: Icon,
  badge,
  active = false,
  disabled = false,
  ...props
}: TabNavItemProps) {
  const itemRef = React.useRef<HTMLLIElement | null>(null);

  React.useEffect(() => {
    if (!active) return;
    const itemEl = itemRef.current;
    if (!itemEl) return;
    const node = itemEl.querySelector<HTMLElement>(
      '[data-slot="tab-nav-item"]'
    );
    if (!node) return;
    scrollActiveTabIntoView(node, '[data-slot="tab-nav-list"]');
  }, [active]);

  const childElement = children as React.ReactElement<
    React.AnchorHTMLAttributes<HTMLAnchorElement>
  >;

  return (
    <NavigationMenuPrimitive.Item ref={itemRef}>
      <NavigationMenuPrimitive.Link
        data-slot="tab-nav-item"
        asChild
        active={active}
        className={tabsItemVariants({ activeSelector: "active", className })}
        {...props}
      >
        {React.cloneElement(childElement, {
          "aria-disabled": disabled ? "true" : undefined,
          onClick: disabled
            ? (e: React.MouseEvent) => {
                e.preventDefault();
              }
            : childElement.props.onClick,
          children: (
            <>
              {Icon && <Icon className="pointer-events-none size-4" />}
              {childElement.props.children}
              {badge !== undefined && (
                <Badge
                  semantic="neutral"
                  emphasis="default"
                  className="pointer-events-none ml-1"
                >
                  {badge}
                </Badge>
              )}
            </>
          ),
        })}
      </NavigationMenuPrimitive.Link>
    </NavigationMenuPrimitive.Item>
  );
}

export { TabNav, TabNavList, TabNavItem };
